/* ============================================================
   Homepage header state + category takeover
   ------------------------------------------------------------
   Two independent controllers share this file:

   1. Header transparency. Transparent and overlaying the banner at
      the top of the homepage, solid and pinned once scrolled past
      the threshold.

      .header-solid is what makes the header stick - the theme's own
      "Sticky header" setting relies on rules in section-header.css,
      which this theme does not have, so it is inert. All sticky
      behaviour on the homepage comes from this file.

   2. The mobile category takeover. Opens from .lx-trigger, closes on
      Escape, on either close control, and on the Android back
      button. Focus is trapped while open and returned to the trigger
      on close, and the page behind is scroll-locked.

   Both are guarded: the header drawer render is commented out in
   header.liquid, and the takeover only exists when the category nav
   is switched on, so neither element is assumed present.
   ============================================================ */

(function () {
  'use strict';

  /* ==========================================================
     1 · Header transparency
     ========================================================== */
  function initHeaderState() {
    var header = document.querySelector('.header-wrapper');
    if (!header) return null;

    // The drawer is currently not rendered. querySelector('details') would
    // otherwise grab the search modal, which is not a menu.
    var menuDrawer = document.querySelector('header-drawer details');
    var menuButton = document.querySelector('.header__icon--menu');

    // Distance over which the header darkens from fully transparent to
    // fully black, and the point at which the compact class lands.
    var SOLID_AT = 100;
    var takeoverOpen = false;

    var isHomepage =
      document.body.classList.contains('template-index') ||
      window.location.pathname === '/' ||
      window.location.pathname === '/index';

    // --lx-solid is a 0-to-1 ramp read by both the background-color and
    // the ::before scrim's opacity, so they cross-fade together. Writing
    // it every frame is what makes the darkening gradual rather than a
    // jump at the threshold.
    function setProgress(value) {
      header.style.setProperty('--lx-solid', String(value));
    }

    function setSolid() {
      setProgress(1);
      header.classList.add('header-solid');
      header.classList.remove('header-transparent');
    }

    function setTransparent() {
      header.classList.add('header-transparent');
      header.classList.remove('header-solid');
    }

    function menuIsOpen() {
      return takeoverOpen || (!!menuDrawer && menuDrawer.hasAttribute('open'));
    }

    function updateHeaderState() {
      if (!isHomepage) {
        setSolid();
        return;
      }

      if (menuIsOpen()) {
        setSolid();
        return;
      }

      var offset = window.scrollY || window.pageYOffset || 0;
      var progress = offset / SOLID_AT;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      setProgress(progress);

      // The class no longer carries the colour - only the compact padding
      // and the smaller logo. By the time it lands the background is
      // already fully black, so the swap is invisible.
      if (progress >= 1) {
        header.classList.add('header-solid');
        header.classList.remove('header-transparent');
      } else {
        setTransparent();
      }
    }

    // rAF-throttled scroll: the old version ran the handler on every
    // scroll event, which fires far more often than the screen repaints.
    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          updateHeaderState();
          ticking = false;
        });
      },
      { passive: true }
    );

    if (menuDrawer) {
      // Covers open and close in one listener, rather than guessing
      // intent from a click before the state has changed.
      menuDrawer.addEventListener('toggle', updateHeaderState);
    }

    if (menuButton && menuDrawer) {
      // The drawer opens after the click, so go solid immediately to
      // avoid a frame of transparent header behind an open menu.
      menuButton.addEventListener('click', function () {
        if (!menuDrawer.hasAttribute('open')) setSolid();
      });
    }

    // Runs once on init as well as on scroll, so a reload partway down the
    // page starts at the right value instead of animating in from zero.
    updateHeaderState();

    // Let the takeover tell us it is open, so the homepage header does
    // not sit transparent behind a solid panel.
    return function (open) {
      takeoverOpen = open;
      updateHeaderState();
    };
  }

  /* ==========================================================
     2 · Mobile category takeover
     ========================================================== */
  function initTakeover(notifyHeader) {
    var trigger = document.getElementById('LxTrigger');
    var panel = document.getElementById('LxTakeover');
    if (!trigger || !panel) return;

    // The panel is rendered inside .section-header, which carries its
    // own z-index and therefore its own stacking context. A fixed
    // element in there can be painted under the cart drawer. Moving it
    // to <body> puts it at the top level where it belongs.
    if (panel.parentElement !== document.body) {
      document.body.appendChild(panel);
    }

    var root = document.documentElement;
    var lastFocused = null;
    var historyPushed = false;

    function focusable() {
      return panel.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    }

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    function open() {
      if (isOpen()) return;

      lastFocused = document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      root.classList.add('lx-nav-open');
      if (notifyHeader) notifyHeader(true);

      // People reach for the Android back button before they look for
      // a close control. Pushing a state means back closes the panel
      // instead of leaving the store.
      try {
        history.pushState({ lxNav: true }, '');
        historyPushed = true;
      } catch (e) {
        historyPushed = false;
      }

      var items = focusable();
      if (items.length) items[0].focus();
    }

    function close(fromPopState) {
      if (!isOpen()) return;

      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      root.classList.remove('lx-nav-open');
      if (notifyHeader) notifyHeader(false);

      // Only unwind the history entry if we are not already handling a
      // popstate, or we would go back twice and leave the page.
      if (historyPushed && !fromPopState) {
        historyPushed = false;
        try {
          history.back();
        } catch (e) {
          /* no-op */
        }
      }
      if (fromPopState) historyPushed = false;

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    trigger.addEventListener('click', function () {
      if (isOpen()) {
        close();
      } else {
        open();
      }
    });

    Array.prototype.forEach.call(panel.querySelectorAll('[data-lx-close]'), function (el) {
      el.addEventListener('click', function () {
        close();
      });
    });

    // Following a link inside the panel navigates away; drop the extra
    // history entry first so back does not land on the open panel.
    Array.prototype.forEach.call(panel.querySelectorAll('a[href]'), function (el) {
      el.addEventListener('click', function () {
        if (historyPushed) {
          historyPushed = false;
          try {
            history.replaceState({}, '');
          } catch (e) {
            /* no-op */
          }
        }
        root.classList.remove('lx-nav-open');
      });
    });

    window.addEventListener('popstate', function () {
      if (isOpen()) close(true);
    });

    document.addEventListener('keydown', function (event) {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = focusable();
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Rotating to landscape or resizing past the breakpoint hides the
    // panel by media query; close it properly so the scroll lock and
    // aria state do not persist on a page the user can now scroll.
    window.addEventListener('resize', function () {
      if (isOpen() && window.innerWidth >= 750) close();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    var notifyHeader = initHeaderState();
    initTakeover(notifyHeader);
  });
})();